<!-- 
Dialog for creating a new levy collection.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog sm">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Start a new levy collection</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="listing-levy-collections" section="creating"/>
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout column>
              <v-flex xs-12>
                <v-radio-group :value="state.type" @change="update($event)">
                  <v-radio 
                    v-for="type in types" 
                    :key="type.value" :label="getLabel(type)" :value="type.value" 
                    color="primary"
                  />
                </v-radio-group>
              </v-flex>
              <v-flex xs-12>
                <v-alert v-if="valid" :value="true" type="success">
                  Levy fees & rates, risk factors, notice templates and invoice template will be copied from the
                  {{ getYear(state.type) - 1 }} {{ lookups.levyCollectionTypeDescriptions[state.type] }} levy collection
                  and can then be updated as required.
                </v-alert>
                <v-alert v-else :value="true" type="info">
                  Levy fees & rates, risk factors, notice templates and invoice template will be
                  copied from the prior year's levy collection and can then be updated as required.
                </v-alert>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
          <v-btn 
            ref="save-button" :disabled="!valid || busy" flat color="success" 
            type="submit" @click.prevent="save"
          >Start</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collections/action-types";
import * as mutation from "@/store/levy-collections/mutation-types";
import filterMixins from "@/mixins/filter.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import _ from "underscore";

export default {
  mixins: [ruleMixins, lookupMixins, filterMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.create;
    },
    listing() {
      return this.$store.state.levyCollections.listing;
    },
    busy() {
      return this.state.saving || this.listing.loading;
    },
    valid() {
      return this.state.type != null;
    },
    types() {
      return _(this.lookups.levyCollectionTypes).filter(k => !k.attributes.includes("IsSandbox"));
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        this.$store.commit(mutation.CREATE_INITIALISE);
        if (!this.listing.loaded) {
          this.$store.dispatch(action.LIST);
        }
      }
    }
  },
  methods: {
    update(newType) {
      this.$store.commit(mutation.CREATE_TYPE, newType);
    },
    async save() {
      const succeeded = await this.$store.dispatch(action.CREATE);
      this.hide();
      if (succeeded) {
        this.$router.push(`/levy-collections/${this.state.data.id}/setup`);
      }
    },
    hide() {
      this.$emit("update:show", false);
    },
    getLabel(type) {
      if (this.listing.loaded) {
        const year = this.getYear(type.value);
        return `Start the ${year} ${type.text} levy collection`;
      } else {
        return `Start levy collection for ${type.text}`;
      }
    },
    getYear(type) {
      const levyCollections = _(this.listing.data).filter(r => r.type == type);
      return _(levyCollections).max(r => r.year).year + 1;
    }
  }
};
</script>
